import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  contractualTerms,
  operations,
  type ProposalCoverage,
  type ProposalCoverageInput,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatMoney,
  moneyInputError,
  moneyToInput,
  toMoney,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

interface LinhaEditavel {
  chave: string;
  id?: string | undefined;
  coverageId?: string | undefined;
  nome: string;
  valor: string;
  premio: string;
  inicio: string;
  prazo: string;
  fim: string;
  premiumCalc: string;
  insuredCalc: string;
  calcParams: Record<string, string>;
}

let proximaChave = 0;

function linhaDe(cobertura: ProposalCoverage): LinhaEditavel {
  proximaChave += 1;
  return {
    chave: `cobertura-${proximaChave}`,
    id: cobertura.id,
    coverageId: cobertura.coverage_id ?? undefined,
    nome: cobertura.name,
    valor: moneyToInput(cobertura.insured_amount),
    premio: cobertura.premium
      ? cobertura.premium.amount === 0
        ? '0'
        : moneyToInput(cobertura.premium)
      : '',
    inicio: cobertura.coverage_start ?? '',
    prazo: cobertura.term_days ? String(cobertura.term_days) : '',
    fim: cobertura.coverage_end ?? '',
    premiumCalc: cobertura.premium_calc,
    insuredCalc: cobertura.insured_amount_calc,
    calcParams: cobertura.calc_params,
  };
}

function linhaNova(): LinhaEditavel {
  proximaChave += 1;
  return {
    chave: `cobertura-${proximaChave}`,
    nome: '',
    valor: '',
    premio: '',
    inicio: '',
    prazo: '',
    fim: '',
    premiumCalc: '',
    insuredCalc: '',
    calcParams: {},
  };
}

/**
 * Coberturas Adicionais do Resumo da cotacao (COT-056 a COT-062).
 *
 * No OnPoint o valor e o premio da cobertura sao calculados pela configuracao da
 * modalidade. As regras estao pendentes (MOD-005, MOD-006, MOD-008, PRC-004), e aqui
 * os dois sao digitados: o valor por quem edita a IS da cotacao, o premio por quem
 * tem a alcada do premio. Inicio, prazo e fim ficam como digitados, porque a
 * convencao entre eles e PRC-008. A trava por etapa e a permissao sao do servidor; a
 * tela so desabilita o que ele recusaria.
 */
export function CoveragesSection({
  operationId,
  canEditStructure,
  canEditTerms,
  canEditPremium,
}: {
  operationId: string;
  canEditStructure: boolean;
  canEditTerms: boolean;
  canEditPremium: boolean;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [linhas, setLinhas] = useState<LinhaEditavel[] | null>(null);

  const consulta = useQuery({
    queryKey: ['operations', operationId, 'coverages'],
    queryFn: ({ signal }) => operations.coverages(operationId, signal),
  });

  // As coberturas do cadastro da modalidade (P-COT-2): a cobertura ligada traz o
  // objeto e as condicoes especiais dela para o editor "Objetos".
  const doCadastro = useQuery({
    queryKey: ['operations', operationId, 'additional-coverage-options'],
    queryFn: ({ signal }) => contractualTerms.coverageOptions(operationId, signal),
    enabled: linhas !== null,
  });

  const gravar = useMutation({
    mutationFn: (itens: ProposalCoverageInput[]) =>
      operations.replaceCoverages(operationId, itens),
    onSuccess: () => {
      toast.success('Coberturas adicionais gravadas.');
      setLinhas(null);
      // A cobertura ligada ao cadastro muda os textos da cotacao (P-COT-2).
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
  });

  const podeEditar = canEditStructure || canEditTerms || canEditPremium;
  const editando = linhas !== null;

  function alterar(chave: string, campo: keyof LinhaEditavel, valor: string) {
    setLinhas((atuais) =>
      (atuais ?? []).map((linha) =>
        linha.chave === chave ? { ...linha, [campo]: valor } : linha,
      ),
    );
  }

  const erros = (linhas ?? []).flatMap((linha) => [
    moneyInputError(linha.valor),
    linha.premio ? moneyInputError(linha.premio) : undefined,
  ]);
  const invalido = erros.some((erro) => erro !== undefined);

  function salvar() {
    if (!linhas || invalido) return;
    gravar.mutate(
      linhas.map((linha) => ({
        ...(linha.id ? { id: linha.id } : {}),
        ...(linha.coverageId ? { coverage_id: linha.coverageId } : {}),
        name: linha.nome,
        insured_amount: toMoney(linha.valor || '0'),
        ...(linha.premio ? { premium: toMoney(linha.premio) } : {}),
        ...(linha.inicio ? { coverage_start: linha.inicio } : {}),
        ...(linha.prazo ? { term_days: Number(linha.prazo) } : {}),
        ...(linha.fim ? { coverage_end: linha.fim } : {}),
        ...(linha.insuredCalc ? { insured_amount_calc: linha.insuredCalc } : {}),
        ...(linha.premiumCalc ? { premium_calc: linha.premiumCalc } : {}),
        calc_params: linha.calcParams,
      })),
    );
  }

  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  /** As coberturas do cadastro, com a que a linha ja aponta mesmo fora da lista. */
  function opcoesDoCadastro(atual: string | undefined) {
    const opcoes = (doCadastro.data ?? []).map((opcao) => ({
      value: opcao.id,
      label: opcao.insurer_reference_code
        ? `${opcao.name} (${opcao.insurer_reference_code})`
        : opcao.name,
    }));
    if (atual && !opcoes.some((opcao) => opcao.value === atual)) {
      opcoes.unshift({ value: atual, label: 'Cobertura fora da lista da modalidade' });
    }
    return opcoes;
  }

  return (
    <Card>
      <CardHeader
        title="Coberturas Adicionais"
        description="Valor e premio digitados: o calculo da cobertura pela modalidade ainda e regra pendente."
        action={
          podeEditar && !editando && !consulta.isPending ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setLinhas((consulta.data ?? []).map(linhaDe))}
            >
              Editar coberturas
            </Button>
          ) : null
        }
      />
      <CardBody className="flex flex-col gap-4">
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as coberturas">
            {consulta.error.message}
          </Alert>
        ) : null}

        {!editando ? (
          <DataTable
            columns={[
              {
                key: 'nome',
                header: 'Coberturas Adicionais',
                render: (c: ProposalCoverage) => (
                  <span>
                    {c.name}
                    {c.coverage_id ? (
                      <span className="ml-1.5 text-2xs text-ink-500">
                        (do cadastro)
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'valor',
                header: 'Valor',
                align: 'right',
                render: (c) => formatMoney(c.insured_amount),
              },
              {
                key: 'premio',
                header: 'Premio',
                align: 'right',
                render: (c) =>
                  c.premium ? (
                    formatMoney(c.premium)
                  ) : (
                    <span className="text-ink-400">nao informado</span>
                  ),
              },
              {
                key: 'inicio',
                header: 'Inicio da vigencia',
                hideBelow: 'md',
                render: (c) => formatCivilDate(c.coverage_start) || '-',
              },
              {
                key: 'prazo',
                header: 'Prazo (dias)',
                align: 'right',
                hideBelow: 'md',
                render: (c) => (c.term_days ? String(c.term_days) : '-'),
              },
              {
                key: 'fim',
                header: 'Fim da vigencia',
                hideBelow: 'md',
                render: (c) => formatCivilDate(c.coverage_end) || '-',
              },
            ]}
            items={consulta.data ?? []}
            keyOf={(c) => c.id}
            loading={consulta.isPending}
            empty={
              <p className="text-sm text-ink-500">
                Nenhuma cobertura adicional nesta cotacao.
              </p>
            }
          />
        ) : (
          <div className="flex flex-col gap-4">
            {gravar.error ? (
              <Alert tone="danger" title="Nao foi possivel gravar">
                {gravar.error.message}
                {typeof apiError?.details?.['index'] === 'number'
                  ? ` (cobertura ${Number(apiError.details['index']) + 1})`
                  : ''}
              </Alert>
            ) : null}
            {linhas.length === 0 ? (
              <p className="text-sm text-ink-500">Nenhuma cobertura na lista.</p>
            ) : null}
            {linhas.map((linha, indice) => (
              <fieldset
                key={linha.chave}
                aria-label={`Cobertura ${indice + 1}`}
                className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-3"
              >
                <Select
                  label="Cobertura do cadastro"
                  placeholder="Sem ligacao com o cadastro"
                  value={linha.coverageId ?? ''}
                  disabled={!canEditStructure}
                  options={opcoesDoCadastro(linha.coverageId)}
                  onChange={(event) => {
                    const escolhida = (doCadastro.data ?? []).find(
                      (opcao) => opcao.id === event.target.value,
                    );
                    setLinhas((atuais) =>
                      (atuais ?? []).map((item) =>
                        item.chave === linha.chave
                          ? {
                              ...item,
                              coverageId: event.target.value || undefined,
                              nome: item.nome || escolhida?.name || '',
                            }
                          : item,
                      ),
                    );
                  }}
                />
                <Field
                  label="Nome da cobertura"
                  value={linha.nome}
                  disabled={!canEditStructure}
                  onChange={(event) => alterar(linha.chave, 'nome', event.target.value)}
                />
                <MoneyField
                  label="Valor da cobertura (R$)"
                  value={linha.valor}
                  disabled={!canEditStructure}
                  error={moneyInputError(linha.valor)}
                  onValueChange={(texto) => alterar(linha.chave, 'valor', texto)}
                />
                <MoneyField
                  label="Premio da cobertura (R$)"
                  value={linha.premio}
                  disabled={!canEditPremium}
                  error={linha.premio ? moneyInputError(linha.premio) : undefined}
                  onValueChange={(texto) => alterar(linha.chave, 'premio', texto)}
                />
                <Field
                  label="Inicio da cobertura"
                  type="date"
                  value={linha.inicio}
                  disabled={!canEditTerms}
                  onChange={(event) =>
                    alterar(linha.chave, 'inicio', event.target.value)
                  }
                />
                <Field
                  label="Prazo da cobertura (dias)"
                  inputMode="numeric"
                  value={linha.prazo}
                  disabled={!canEditTerms}
                  onChange={(event) =>
                    alterar(linha.chave, 'prazo', event.target.value.replace(/\D/g, ''))
                  }
                />
                <Field
                  label="Fim da cobertura"
                  type="date"
                  value={linha.fim}
                  disabled={!canEditTerms}
                  onChange={(event) => alterar(linha.chave, 'fim', event.target.value)}
                />
                {canEditStructure ? (
                  <div className="sm:col-span-3">
                    <AcaoComIcone
                      acao="remover"
                      rotulo="Remover cobertura"
                      texto
                      onClick={() =>
                        setLinhas((atuais) =>
                          (atuais ?? []).filter((item) => item.chave !== linha.chave),
                        )
                      }
                    />
                  </div>
                ) : null}
              </fieldset>
            ))}
            <p className="text-2xs text-ink-500">
              A cobertura ligada ao cadastro da modalidade traz o objeto e as condicoes
              especiais dela para Objetos; sem ligacao, ela e escrita so pelo nome.
              Inicio, prazo e fim ficam como digitados.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              {canEditStructure ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setLinhas((atuais) => [...(atuais ?? []), linhaNova()])
                  }
                >
                  Adicionar cobertura
                </Button>
              ) : null}
              <Button size="sm" variant="secondary" onClick={() => setLinhas(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                loading={gravar.isPending}
                disabled={invalido}
                onClick={salvar}
              >
                Salvar coberturas
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
